"use client";

import { useQuery } from "@tanstack/react-query";
import { config } from "@/config/env";
import { useAuth } from "@clerk/clerk-react";
import { useTranslation } from "react-i18next";
import { useMemo, useCallback } from "react";

/**
 * TikTok API helper — calls tiktok endpoints directly.
 */
function useTikTokApi() {
  const { getToken } = useAuth();
  const { i18n } = useTranslation();

  const apiCall = useCallback(
    async <T>(method: string, path: string, body?: unknown): Promise<T> => {
      const token = await getToken();
      const headers: Record<string, string> = {
        "Accept-Language": i18n.language,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      if (body) {
        headers["Content-Type"] = "application/json";
      }

      const response = await fetch(`${config.apiUrl}${path}`, {
        method,
        headers,
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Request failed" }));
        const err = new Error(error.message || `HTTP ${response.status}`) as Error & { code?: string };
        err.code = error.code;
        throw err;
      }

      return response.json() as Promise<T>;
    },
    [getToken, i18n.language]
  );

  return apiCall;
}

// --- Types matching backend ---

export interface TikTokAccount {
  id: string;
  userId: string;
  tiktokOpenId: string;
  tiktokUsername: string;
  displayName: string;
  avatarUrl?: string;
  connectedAt: string;
  updatedAt: string;
}

export interface TikTokPost {
  id: string;
  userId: string;
  jobId: string;
  tiktokAccountId: string;
  publishId?: string;
  tiktokVideoId?: string;
  caption: string;
  hashtags: string[];
  privacyLevel: "PUBLIC_TO_EVERYONE" | "MUTUAL_FOLLOW_FRIENDS" | "SELF_ONLY" | "FOLLOWER_OF_CREATOR";
  disableDuet: boolean;
  disableStitch: boolean;
  disableComment: boolean;
  isCommercialContent: boolean;
  isAiGenerated: boolean;
  status: "pending" | "uploading" | "publishing" | "published" | "scheduled" | "failed";
  errorMessage?: string;
  scheduledAt?: string;
  publishedAt?: string;
  tiktokShareUrl?: string;
  createdAt: string;
}

/**
 * Fetch connected TikTok accounts for the current user.
 */
export function useTikTokAccounts() {
  const apiCall = useTikTokApi();

  return useQuery({
    queryKey: ["tiktok-accounts"],
    queryFn: () =>
      apiCall<{ accounts: TikTokAccount[] }>("GET", "/tiktok/accounts"),
    staleTime: 30_000,
  });
}

/**
 * Fetch TikTok posts for a specific video job with smart polling.
 * Polls every 3s while any post is still processing.
 */
export function useTikTokPosts(jobId: string | undefined) {
  const apiCall = useTikTokApi();

  return useQuery({
    queryKey: ["tiktok-posts", jobId],
    queryFn: () =>
      apiCall<{ posts: TikTokPost[] }>("GET", `/tiktok/posts/by-job/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const posts = query.state.data?.posts;
      if (!posts) return false;
      const hasActive = posts.some(
        (p) => p.status === "pending" || p.status === "uploading" || p.status === "publishing"
      );
      return hasActive ? 3000 : false;
    },
  });
}

/**
 * TikTok API mutations (non-hooks, for use in callbacks).
 */
export function useTikTokActions() {
  const apiCall = useTikTokApi();

  return useMemo(
    () => ({
      getAuthUrl: () =>
        apiCall<{ authUrl: string }>("GET", "/tiktok/auth/url"),

      handleCallback: (code: string, state: string) =>
        apiCall<{ success: boolean; accountId: string; tiktokUsername: string }>(
          "POST",
          "/tiktok/auth/callback",
          { code, state }
        ),

      disconnect: (accountId: string) =>
        apiCall<{ success: boolean }>("DELETE", `/tiktok/accounts/${accountId}`),

      publish: (params: {
        jobId: string;
        accountId: string;
        caption: string;
        hashtags: string[];
        privacyLevel: string;
        disableDuet?: boolean;
        disableStitch?: boolean;
        disableComment?: boolean;
        isCommercialContent?: boolean;
        isAiGenerated?: boolean;
        scheduledAt?: string;
      }) =>
        apiCall<{ postId: string; status: string }>("POST", "/tiktok/publish", params),

      getPost: (postId: string) =>
        apiCall<{ post: TikTokPost }>("GET", `/tiktok/posts/${postId}`),
    }),
    [apiCall]
  );
}
